import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Edit, Trash2, Settings } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { BusService } from '../../services/busService';
import { BusType, CreateBusTypeForm, UpdateBusTypeForm } from '../../types';

interface BusTypeModalProps {
  onClose: () => void;
  onRefresh: () => void;
}

export const BusTypeModal: React.FC<BusTypeModalProps> = ({ onClose, onRefresh }) => {
  const [busTypes, setBusTypes] = useState<BusType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<BusType | null>(null);
  const [formData, setFormData] = useState<CreateBusTypeForm>({
    name: '',
    description: '',
    basePriceMultiplier: 1.0,
    defaultSeats: 50,
    defaultAmenities: [],
    defaultFeatures: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadBusTypes();
  }, []);

  const loadBusTypes = async () => {
    try {
      setLoading(true);
      const types = await BusService.getBusTypes();
      setBusTypes(types);
    } catch (error: any) {
      console.error('Failed to load bus types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      basePriceMultiplier: 1.0,
      defaultSeats: 50,
      defaultAmenities: [],
      defaultFeatures: [],
    });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (busType: BusType) => {
    setEditingType(busType);
    setFormData({
      name: busType.name,
      description: busType.description,
      basePriceMultiplier: busType.basePriceMultiplier,
      defaultSeats: busType.defaultSeats,
      defaultAmenities: busType.defaultAmenities,
      defaultFeatures: busType.defaultFeatures,
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this bus type?')) {
      try {
        // Note: In a real app, you'd need a delete method in BusService
        console.log('Delete bus type:', id);
        await loadBusTypes();
        onRefresh();
      } catch (error: any) {
        console.error('Failed to delete bus type:', error);
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.basePriceMultiplier < 0.1 || formData.basePriceMultiplier > 10) {
      newErrors.basePriceMultiplier = 'Price multiplier must be between 0.1 and 10';
    }

    if (formData.defaultSeats < 1 || formData.defaultSeats > 100) {
      newErrors.defaultSeats = 'Default seats must be between 1 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (editingType) {
        await BusService.updateBusType(editingType.id, formData as UpdateBusTypeForm);
      } else {
        await BusService.createBusType(formData);
      }
      
      await loadBusTypes();
      onRefresh();
      setShowForm(false);
      setEditingType(null);
    } catch (error: any) {
      console.error('Failed to save bus type:', error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-2xl">
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">Loading bus types...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Settings className="h-6 w-6 text-primary-600 mr-3" />
                <h2 className="text-xl font-semibold text-secondary-900">
                  {editingType ? 'Edit Bus Type' : 'Create Bus Type'}
                </h2>
              </div>
              <Button variant="outline" onClick={() => setShowForm(false)} className="p-2">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Standard, Premium, Luxury"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this bus type..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.description ? 'border-red-500' : 'border-secondary-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Base Price Multiplier *
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={formData.basePriceMultiplier}
                    onChange={(e) => setFormData(prev => ({ ...prev, basePriceMultiplier: parseFloat(e.target.value) || 1.0 }))}
                    className={errors.basePriceMultiplier ? 'border-red-500' : ''}
                  />
                  {errors.basePriceMultiplier && (
                    <p className="mt-1 text-sm text-red-600">{errors.basePriceMultiplier}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    Multiplier for base route pricing (1.0 = standard, 1.5 = premium, etc.)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Default Seats *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.defaultSeats}
                    onChange={(e) => setFormData(prev => ({ ...prev, defaultSeats: parseInt(e.target.value) || 50 }))}
                    className={errors.defaultSeats ? 'border-red-500' : ''}
                  />
                  {errors.defaultSeats && (
                    <p className="mt-1 text-sm text-red-600">{errors.defaultSeats}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  {editingType ? 'Update Bus Type' : 'Create Bus Type'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Settings className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-secondary-900">Bus Types</h2>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleCreate} className="flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Add Bus Type
              </Button>
              <Button variant="outline" onClick={onClose} className="p-2">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {busTypes.length === 0 ? (
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No bus types found</h3>
              <p className="text-secondary-500 mb-4">Create your first bus type to get started.</p>
              <Button onClick={handleCreate} className="flex items-center mx-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Bus Type
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {busTypes.map((busType) => (
                <Card key={busType.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">{busType.name}</h3>
                      <p className="text-sm text-secondary-600">{busType.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(busType)}
                        className="p-2"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(busType.id)}
                        className="p-2 text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-secondary-600">Price Multiplier:</span>
                      <span className="text-sm font-medium text-secondary-900">{busType.basePriceMultiplier}x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-secondary-600">Default Seats:</span>
                      <span className="text-sm font-medium text-secondary-900">{busType.defaultSeats}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-secondary-600">Status:</span>
                      <span className={`text-sm font-medium ${
                        busType.isActive ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {busType.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
